import React from 'react';
import { FaSpinner } from 'react-icons/fa';
import { OrderDetail } from '../../../hooks';
import { OrderStatus, ORDER_STATUS_CONFIG } from '../../../utils/orderUtils';

interface OrderStatusActionsProps {
  order: OrderDetail;
  updating: boolean;
  pendingStatus: OrderStatus | null;
  onStatusUpdate: (status: OrderStatus) => void;
  onConfirmUpdate: () => void;
  onCancelUpdate: () => void;
}

export const OrderStatusActions: React.FC<OrderStatusActionsProps> = ({
  order,
  updating,
  pendingStatus,
  onStatusUpdate,
  onConfirmUpdate,
  onCancelUpdate,
}) => {
  const renderStatusButton = (status: OrderStatus) => {
    const config = ORDER_STATUS_CONFIG[status];
    const IconComponent = config.icon;
    const isActive = order.status === status;
    const isPending = pendingStatus === status;
    const isDisabled = updating || isActive;

    if (isPending && !isActive) {
      return (
        <div key={status} className="w-full space-y-2">
          <div
            className={`w-full p-4 rounded-xl font-semibold text-sm border ${config.buttonActiveClass} animate-pulse`}
          >
            <div className="flex items-center justify-center gap-2">
              <IconComponent />
              <span>{config.text}</span>
            </div>
          </div>
          <div className="text-center text-sm text-gray-600 mb-2">Emin misiniz?</div>
          <div className="flex gap-2">
            <button
              onClick={onConfirmUpdate}
              disabled={updating}
              className="flex-1 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              {updating ? <FaSpinner className="animate-spin mx-auto" /> : 'Evet, Değiştir'}
            </button>
            <button
              onClick={onCancelUpdate}
              disabled={updating}
              className="flex-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
            >
              İptal
            </button>
          </div>
        </div>
      );
    }

    return (
      <button
        key={status}
        onClick={() => onStatusUpdate(status)}
        disabled={isDisabled || pendingStatus !== null}
        className={`w-full p-4 rounded-xl font-semibold text-sm border transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:opacity-50 ${isActive ? config.buttonActiveClass : config.buttonInactiveClass}`}
      >
        <div className="flex items-center justify-center gap-2">
          {updating && order.status === status ? (
            <FaSpinner className="animate-spin" />
          ) : (
            <IconComponent />
          )}
          <span>{config.text}</span>
        </div>
      </button>
    );
  };

  return (
    <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-6 rounded-2xl border border-orange-100 shadow-lg">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-xl flex items-center justify-center">
          <FaSpinner className="text-white text-lg" />
        </div>
        <h3 className="text-xl font-bold text-gray-900">Sipariş Durumu</h3>
      </div>
      <div className="space-y-3">
        {renderStatusButton('preparing')}
        {renderStatusButton('on-the-way')}
        {renderStatusButton('completed')}
        {order.status !== 'cancelled' && renderStatusButton('cancelled')}
      </div>
    </div>
  );
};
